import { Box, Checkbox, Divider, Grid, Stack, Switch, TextField, Typography } from "@mui/material"
import PaperBox from "components/PaperBox"
import MenuIcon from "components/navigation/MenuIcon"
import { FieldArray, useFormikContext } from "formik"
import { t as globalT } from "i18next"
import { useTranslation } from "react-i18next"
import { StyledPrimaryUserAvatar } from "shared/styled/Global"
import { FeatureBox } from "./FeatureBox"
import { ChangeEvent, useEffect, useState } from "react"
import { deviationTemplateKey, deviationType } from "shared/interfaces/deviation"

export const diableIfPartnerOrSystemAdmin = (module: any, modules: any[]) => {
  if (
    module.feature === "PartnerCustomers" &&
    modules?.findIndex((m): any => m.feature === "SystemAdminCustomers" && m.enabled) !== -1
  ) {
    return true
  } else if (
    module.feature === "SystemAdminCustomers" &&
    modules?.findIndex((m): any => m.feature === "PartnerCustomers" && m.enabled) !== -1
  ) {
    return true
  }
}

interface CustomerDeviationSettingProps {
  feature: any
  settingType: string
  settingTypeLabel: string
  formTemplates?: any[]
}

interface CustomerDeviationSettingsValues {
  id: number
  customerDeviationSettings: {
    customerId: number
    formTemplateId: number
    [key: string]: boolean | number
  }[]
}

const CustomerDeviationSetting = ({ feature, settingType, settingTypeLabel, formTemplates }: CustomerDeviationSettingProps) => {
  const { values, setFieldValue } = useFormikContext<CustomerDeviationSettingsValues>()
  const formTemplate = deviationNameToTemplate(feature.feature, formTemplates)

  const handleToggle = (_: ChangeEvent<HTMLInputElement>, checked: boolean): void => {
    const customerDeviationSettings = Array.isArray(values.customerDeviationSettings) ? [...values.customerDeviationSettings] : []
    const existingSettingIndex = customerDeviationSettings.findIndex((setting: any) =>
      setting.customerId === values?.id && setting.formTemplateId === formTemplate?.id
    )

    if (existingSettingIndex !== -1) {
      customerDeviationSettings[existingSettingIndex] = {
        ...customerDeviationSettings[existingSettingIndex],
        [settingType]: checked,
      }
    } else {
      customerDeviationSettings.push({
        customerId: values.id,
        formTemplateId: formTemplate.id,
        [settingType]: checked
      })
    }
    setFieldValue(`customerDeviationSettings`, customerDeviationSettings)
  }

  return (
    <Box>
      <Grid container direction="row" justifyContent="flex-start" alignItems="center">
        <Box>
          <Checkbox
            sx={{ padding: 0 }}
            onChange={handleToggle}
            checked={
              !!values.customerDeviationSettings?.find(
                (setting: any) =>
                  setting.customerId === values?.id &&
                  setting.formTemplateId === formTemplate?.id
              )?.[settingType]
            }
          />
        </Box>
        <Box pl={1}>
          <Typography variant="bodyMedium">
            {settingTypeLabel}
          </Typography>
        </Box>
      </Grid>
    </Box>
  )
}

export const deviationNameToTemplate = (value: string, formTemplates: any) => {
  switch (value) {
    case "DeviationVehicleDamage":
      return formTemplates?.find((template: any) => template.key === "vehicle_damage") || null
    case "DeviationThreatsAndViolence":
      return formTemplates?.find((template: any) => template.key === "threats_and_violence") || null
    case "DeviationWorkingEnvironment":
      return formTemplates?.find((template: any) => template.key === "working_environment") || null
    case "DeviationVastanhedePersonalInjuries":
      return formTemplates?.find((template: any) => template.key === "vastanhede_incident_personal_injuries") || null
    case "DeviationVastanhedeWorkingEnvironment":
      return formTemplates?.find((template: any) => template.key === "vastanhede_working_environment") || null
    case "DeviationTrafficAndCustomerInfo":
      return formTemplates?.find((template: any) => template.key === "traffic_and_customerinfo") || null
    case "DeviationVastanhedeTrafficDisruption":
      return formTemplates?.find((template: any) => template.key === "vastanhede_traffic_disruption") || null
    case "DeviationSuggestionBox":
      return formTemplates?.find((template: any) => template.key === "suggestion_box") || null
    case "DeviationTrafficLog":
      return formTemplates?.find((template: any) => template.key === "traffic_log") || null
    case deviationType.gotlandSchoolBus:
      return formTemplates?.find((template: any) => template.key === deviationTemplateKey.gotlandSchoolBus) || null
    default:
      return value
  }
}

export const ModuleBox = ({
  handleChange,
  handleBlur,
  menuIcon,
  index,
  setFieldValue,
  module,
  arrayName,
  modules,
  values,
  touched,
  errors,
  formTemplateResult
}: any) => {
  const { t } = useTranslation("translation", { keyPrefix: "global.features" })
  const [formTemplates, setFormTemplates] = useState([])

  useEffect(() => {
    if (formTemplateResult?.status !== 2) return
    setFormTemplates(formTemplateResult.data)
  }, [formTemplateResult])

  return (
    <PaperBox>
      <Grid container direction="row" justifyContent="flex-start" spacing={3}>
        <Grid sx={{
          display: { xs: "none", md: "block" }
        }} item xs={3} md={1}>
          <StyledPrimaryUserAvatar aria-label={module?.name}>
            <MenuIcon name={menuIcon} />
          </StyledPrimaryUserAvatar>
        </Grid>
        <Grid item xs={12} md={11}>
          <Grid container direction="row" justifyContent="space-between" alignItems="center">
            <Grid item xs={10} md={11}>
              <Typography variant="h5">
                {t(`title.${module?.feature.toLowerCase()}`, { defaultValue: module?.name })}
              </Typography>
              <Typography variant="bodySmall">
                {t(`description.${module?.feature.toLowerCase()}`, { defaultValue: module?.description })}
              </Typography>
            </Grid>

            <Grid item xs={2} md={1} justifyContent="flex-end" textAlign="right">
              <Switch
                onChange={() => setFieldValue(`${arrayName}.${index}.enabled`, !module?.enabled)}
                checked={module?.enabled}
                disabled={diableIfPartnerOrSystemAdmin(module, modules)}
              />
            </Grid>
          </Grid>

          {module?.enabled && module?.maxUsersEnabled && (
            <Grid item xs={12}>
              <Divider sx={{ marginY: 1 }} light />
              <Grid container direction="row" justifyContent="space-between" alignItems="center">
                <Grid item xs={10} md={9}>
                  <Typography variant="labelMedium" display="block">
                    {globalT("settings.form.max_users")}
                  </Typography>
                  <Typography variant="bodyMedium">
                    {globalT("settings.form.max_users_description")}
                  </Typography>
                </Grid>
                <Grid item xs={2} md={3} justifyContent="flex-end">
                  <TextField
                    size="small"
                    autoComplete="off"
                    fullWidth
                    id={`${arrayName}.${index}.maxUsers`}
                    type="number"
                    value={module?.maxUsers ?? ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    variant="outlined"
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
          {module?.enabled && (
            <FieldArray
              name={`modules.${index}`}
              render={() => (
                <div>
                  {module?.features && module.features.length > 0 && <Divider sx={{ marginY: 1 }} light />}
                  {module?.features &&
                    module.features.length > 0 &&
                    module.features.map((feature: any, featureIndex: number) => {
                      return (
                        <>
                          <FeatureBox
                            index={featureIndex}
                            feature={feature}
                            handleChange={handleChange}
                            handleBlur={handleBlur}
                            menuIcon={module.icon}
                            setFieldValue={setFieldValue}
                            arrayName={`modules.${index}.features.${featureIndex}`}
                          />
                          {module?.feature === "Deviation" && feature?.enabled && (
                            <Box display="flex" flexDirection="column" gap={0.5} mt={1} mb={2}>
                              <CustomerDeviationSetting
                                feature={feature}
                                settingType="autoAssign"
                                settingTypeLabel={t("deviation.auto_assign", "Auto assign submitter's boss")}
                                formTemplates={formTemplates}
                              />
                              <CustomerDeviationSetting
                                feature={feature}
                                settingType="autoNotify"
                                settingTypeLabel={t("deviation.auto_notify", "Auto notify on update")}
                                formTemplates={formTemplates}
                              />
                            </Box>
                          )}
                        </>
                      )
                    })}
                </div>
              )}
            />
          )}
        </Grid>
      </Grid>
    </PaperBox>
  )
}
